import { useReducer, useCallback } from 'react';

const MAX_HISTORY = 50;

function historyReducer(state, action) {
  switch (action.type) {
    case 'SET': {
      // New state — push current to past, clear future
      const newPresent = typeof action.payload === 'function'
        ? action.payload(state.present)
        : action.payload;
      const past = [...state.past, state.present].slice(-MAX_HISTORY);
      return { past, present: newPresent, future: [] };
    }
    case 'UNDO': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      const newPast = state.past.slice(0, -1);
      return { past: newPast, present: previous, future: [state.present, ...state.future] };
    }
    case 'REDO': {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      const newFuture = state.future.slice(1);
      return { past: [...state.past, state.present], present: next, future: newFuture };
    }
    case 'REPLACE': {
      // Replace present without pushing to history (e.g. from server sync)
      return { ...state, present: action.payload };
    }
    default:
      return state;
  }
}

/**
 * useSlideHistory — undo/redo for slide state.
 *
 * @param {Array} initialSlides - initial slides array
 * @returns {{ slides, setSlides, undo, redo, canUndo, canRedo, replaceSlides }}
 */
export default function useSlideHistory(initialSlides = []) {
  const [state, dispatch] = useReducer(historyReducer, {
    past: [],
    present: initialSlides,
    future: [],
  });

  const setSlides = useCallback((slidesOrFn) => {
    dispatch({ type: 'SET', payload: slidesOrFn });
  }, []);

  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);
  const replaceSlides = useCallback((slides) => dispatch({ type: 'REPLACE', payload: slides }), []);

  return {
    slides: state.present,
    setSlides,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    replaceSlides,
  };
}
